import type { Priority, WorkType } from '@rundown/types';
import { clientStatusOfColumn } from '@rundown/domain';
import { type BoardColumn } from '@rundown/types';
import { ROLE_LABELS } from '@rundown/policy';
import { formatHours } from '../../lib/format';
import { PRIORITY_LABELS, STATUS_LABELS, TYPE_LABELS } from '../requests/labels';
import type { ActivityRow } from './types';
import { isRecord } from '../../lib/guards';

/**
 * `detail` (jsonb) rendered as an English sentence.
 *
 * Two things this has to get right that a generic "stringify the diff"
 * renderer would not:
 *
 *  - `kind` is an open set. Another agent is adding `config.changed` while
 *    this file is being written, and more kinds will land after this ships.
 *    Every branch below is reached by matching a known `kind` string; the
 *    fallback for anything else humanises the kind itself ("config changed")
 *    rather than crashing on an unexpected `detail` shape or dumping raw JSON
 *    — the two ways a switch like this usually breaks in production.
 *
 *  - `boardColumn` is an internal concept with internal vocabulary
 *    ('triage', 'staging') that does not appear anywhere in the client's
 *    five-state model. A client CAN legitimately see a `request.updated`
 *    entry for a board move — it is not on the internal-only field list in
 *    requests.policy.ts — so this translates the value through the same
 *    `BOARD_COLUMN_TO_CLIENT_STATUS` map the request DTO itself uses,
 *    rather than surface "moved to staging" to someone who has never been
 *    shown a column called Staging.
 */

function actorOf(row: ActivityRow): string {
  return row.actorName ?? 'Someone';
}

/**
 * "#3666 “Booking widget shows no price”".
 *
 * A deleted request's activity row carries `request_id: null` — the row has to
 * outlive the request, and a foreign key would take it down with it (see
 * RequestsService.remove) — so the number and title in `detail` are the ONLY
 * record of which request this was. Rendering them is not decoration.
 */
function deletedRequestOf(detail: unknown): string {
  if (!isRecord(detail)) return 'a request';
  const number = detail['number'];
  const title = detail['title'];
  const numbered = typeof number === 'number' ? `#${number}` : 'a request';
  return typeof title === 'string' && title !== '' ? `${numbered} “${title}”` : numbered;
}

/** The other half of a merge, named from whichever side is reading it. */
function mergedRequestOf(detail: unknown, side: 'into' | 'from'): string {
  if (!isRecord(detail)) return 'another request';
  const number = detail[side === 'into' ? 'intoNumber' : 'fromNumber'];
  const title = detail[side === 'into' ? 'intoTitle' : 'fromTitle'];
  const numbered = typeof number === 'number' ? `#${number}` : 'another request';
  return typeof title === 'string' && title !== '' ? `${numbered} “${title}”` : numbered;
}

/** "· 3 items returned to the backlog", when a deleted sprint had work in it. */
function returnedOf(detail: unknown): string {
  if (!isRecord(detail)) return '';
  const count = detail['returnedToBacklog'];
  if (typeof count !== 'number' || count <= 0) return '';
  return ` · ${count} ${count === 1 ? 'item' : 'items'} returned to the backlog`;
}

/** "request.updated" -> "request updated", for kinds this file does not know. */
function humanizeKind(kind: string): string {
  return kind.replace(/[._]/g, ' ');
}

const FIELD_LABELS: Readonly<Record<string, string>> = {
  title: 'the title',
  description: 'the description',
  type: 'Type',
  priority: 'Priority',
  approxHours: 'the estimate',
  projectId: 'the project',
  boardColumn: 'Status',
  visibleToClient: 'client visibility',
};

function formatFieldValue(field: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '(none)';
  switch (field) {
    case 'type':
      return TYPE_LABELS[value as WorkType] ?? String(value);
    case 'priority':
      return PRIORITY_LABELS[value as Priority] ?? String(value);
    case 'boardColumn':
      return STATUS_LABELS[clientStatusOfColumn(value as BoardColumn)] ?? String(value);
    case 'approxHours':
      return typeof value === 'number' ? formatHours(value) : String(value);
    case 'visibleToClient':
      return value === true ? 'Yes' : 'No';
    case 'projectId':
      // No project name available at this join — see activity.service.ts,
      // which does not carry one. Naming the field is enough; a raw uuid is
      // not information anyone reading the trail can act on.
      return '';
    default: {
      const text = String(value);
      return text.length > 80 ? `${text.slice(0, 80)}…` : text;
    }
  }
}

function formatFieldChange(field: string, from: unknown, to: unknown): string {
  const label = FIELD_LABELS[field] ?? field;
  if (field === 'projectId') return `changed ${label}`;
  // Ids with no name at this join (same as projectId): say what happened, not which uuid.
  if (field === 'sprintId') {
    if (to === null) return 'took this out of its sprint';
    return from === null ? 'committed this to a sprint' : 'moved this to another sprint';
  }
  if (field === 'ownerId') return to === null ? 'unassigned the owner' : 'changed the owner';
  if (field === 'isHotfix')
    return to === true ? 'moved this to the hotfix track' : 'took this off the hotfix track';
  const fromText = formatFieldValue(field, from);
  const toText = formatFieldValue(field, to);
  return `changed ${label} from ${fromText} to ${toText}`;
}

/** One request.updated activity carries one field's change (see requests.service.ts `update()`). */
function formatUpdated(detail: unknown): string {
  if (!isRecord(detail) || typeof detail['field'] !== 'string') return 'updated this request';
  return formatFieldChange(detail['field'], detail['from'], detail['to']);
}

function formatConfirmed(detail: unknown): string {
  if (!isRecord(detail)) return 'confirmed this request';
  const parts: string[] = [];
  if (typeof detail['type'] === 'string') {
    parts.push(TYPE_LABELS[detail['type'] as WorkType] ?? detail['type']);
  }
  if (typeof detail['project'] === 'string' && detail['project'] !== '') parts.push(detail['project']);
  if (typeof detail['priority'] === 'string') {
    parts.push(PRIORITY_LABELS[detail['priority'] as Priority] ?? detail['priority']);
  }
  if (typeof detail['approxMinutes'] === 'number' && detail['approxMinutes'] > 0) {
    parts.push(`~${formatHours(detail['approxMinutes'] / 60)}`);
  }
  return parts.length > 0 ? `confirmed this request as ${parts.join(' · ')}` : 'confirmed this request';
}

/**
 * Configuration changes, whose `field` is a PATH rather than a plain name.
 *
 * `recordConfigChanges` folds the subject's id into the field so one shape,
 * {field, from, to}, covers every config row in the codebase — which is right
 * for the writer and unreadable for a reader: `people.<uuid>.account.suspended`
 * is not a sentence. The id is dropped here rather than resolved to a name,
 * because the trail carries no join to resolve it with and a bare uuid is not
 * information anyone can act on (same reasoning as `projectId` above).
 */
const ROLE_NAMES: Readonly<Record<string, string>> = ROLE_LABELS;

/**
 * Every `field` shape `recordConfigChanges` actually writes, and the subject
 * prefix each one carries:
 *
 *   people.<uuid>.role                 PeopleService
 *   people.<uuid>.grade                PeopleService
 *   people.<uuid>.allocation           PeopleService
 *   people.<uuid>.account.suspended    AccountsService
 *   project.<uuid>.name                ProjectsService
 *   workspace.monthlyCapacityHours     WorkspaceSettingsService
 *   secret.smtp.password               SecretsService
 *
 * `engineer.<uuid>.*` used to be in this list, written by an EngineersService
 * that no longer exists — grades and allocations are facts about a person now
 * and are written under `people.` with everything else about them.
 *
 * The first four carry a uuid and the last two do not, which is why this strips
 * an optional id rather than requiring one. Getting that wrong is not cosmetic:
 * a missed prefix renders "changed project.5f3a…-….name from X to Y" at the
 * reader, which is the exact thing this function exists to prevent.
 */
const SUBJECT_PREFIX = /^(?:people|project|workspace|secret)\.(?:[0-9a-f-]{8,}\.)?/i;

const CONFIG_LABELS: Readonly<Record<string, string>> = {
  'account.password': 'set a password for someone',
  'account.mustChangePassword': 'changed whether they must pick a new password',
  invited: 'added a person',
};

function formatConfigChanged(detail: unknown): string {
  if (!isRecord(detail) || typeof detail['field'] !== 'string') return 'changed a setting';

  const tail = detail['field'].replace(SUBJECT_PREFIX, '');
  const from = detail['from'];
  const to = detail['to'];

  switch (tail) {
    // Both directions share one field, and rendering a restore as "suspended an
    // account" would be worse than showing nothing at all.
    case 'account.suspended':
      return to === false ? 'restored an account' : 'suspended an account';
    // The reason is mandatory on the way in precisely so somebody can read it
    // later; printing "recorded why" and dropping it answers nothing.
    case 'account.suspendReason':
      return typeof to === 'string' ? `suspended an account: ${to}` : 'recorded a suspension reason';
    // These two used to render without their values, so "changed someone's
    // access" read identically for a removal and a reinstatement.
    case 'role':
      return `changed someone's role to ${ROLE_NAMES[String(to)] ?? String(to)}`;
    case 'active':
      return to === true ? 'restored access to this workspace' : 'removed access to this workspace';
    default:
      return CONFIG_LABELS[tail] ?? formatFieldChange(tail, from, to);
  }
}

function sprintNameOf(detail: unknown): string {
  return isRecord(detail) && typeof detail['sprintName'] === 'string' ? detail['sprintName'] : 'the sprint';
}

function daysOf(detail: unknown): string {
  if (!isRecord(detail) || typeof detail['days'] !== 'number') return '';
  return ` by ${detail['days']} day${detail['days'] === 1 ? '' : 's'}`;
}

/** 'add' → "bring this into", 'remove' → "take this out of". */
function planChangeVerb(detail: unknown): string {
  return isRecord(detail) && detail['role'] === 'remove' ? 'take this out of' : 'bring this into';
}

function planChangeDone(detail: unknown): string {
  return isRecord(detail) && detail['role'] === 'remove' ? 'taken out of' : 'brought into';
}

function reasonOf(detail: unknown, key: 'reason' | 'note' = 'reason'): string {
  if (!isRecord(detail) || typeof detail[key] !== 'string' || detail[key] === '') return '';
  const text = detail[key] as string;
  return `: “${text.length > 120 ? `${text.slice(0, 120)}…` : text}”`;
}

/**
 * Renders one activity row as a full English sentence, e.g. "Imran changed
 * Type from Feature to Bug." Falls back to a humanised kind for anything this
 * does not recognise, per the file header — it never throws and never shows
 * raw JSON.
 */
export function formatActivity(row: ActivityRow): string {
  const actor = actorOf(row);

  switch (row.kind) {
    case 'request.created':
      return `${actor} posted this request`;
    case 'request.updated':
      return `${actor} ${formatUpdated(row.detail)}`;
    case 'request.confirmed':
      return `${actor} ${formatConfirmed(row.detail)}`;
    case 'request.asked_client':
      return `${actor} asked the client for more detail`;
    case 'message.posted':
      return `${actor} posted a message`;
    case 'message.edited':
      return `${actor} edited a message`;
    case 'message.deleted':
      return `${actor} deleted a message`;
    case 'request.description_changed':
      return `${actor} edited the opening message`;
    case 'thread.converted':
      // No actor means triage did it on its own, not a person asking.
      return `${row.actorName === null ? '@rundown' : actor} filed this chat as an issue`;
    case 'thread.kept_as_chat':
      return `${actor} kept this as a chat rather than an issue`;
    case 'thread.deleted':
      return `${actor} deleted a thread`;
    case 'config.changed':
      return `${actor} ${formatConfigChanged(row.detail)}`;
    case 'sprint.locked':
      return `${actor} started ${sprintNameOf(row.detail)} · scope locked`;
    case 'sprint.carried_over':
      return `${actor} carried this over from ${sprintNameOf(row.detail)} to the top of the backlog`;
    case 'request.deleted':
      return `${actor} deleted ${deletedRequestOf(row.detail)}`;
    case 'request.merged_duplicate':
      return `${actor} merged this into ${mergedRequestOf(row.detail, 'into')} as a duplicate`;
    case 'request.duplicate_merged':
      return `${actor} merged ${mergedRequestOf(row.detail, 'from')} into this as a duplicate`;
    case 'sprint.unlocked':
      return `${actor} unlocked ${sprintNameOf(row.detail)} · it is being planned again`;
    case 'sprint.deleted':
      return `${actor} deleted ${sprintNameOf(row.detail)}${returnedOf(row.detail)}`;
    case 'sprint.delayed':
      return `${actor} delayed ${sprintNameOf(row.detail)}${daysOf(row.detail)}`;
    case 'request.placement_suggested':
      return `${actor} suggested this for ${sprintNameOf(row.detail)}`;
    case 'request.placement_withdrawn':
      return `${actor} withdrew their sprint suggestion`;
    case 'sprint.plan_change_raised':
      return `${actor} asked to ${planChangeVerb(row.detail)} ${sprintNameOf(row.detail)}${reasonOf(row.detail)}`;
    case 'sprint.plan_change_agreed':
      return `${actor} agreed a change of plan · ${planChangeDone(row.detail)} ${sprintNameOf(row.detail)}${reasonOf(row.detail, 'note')}`;
    case 'sprint.plan_change_declined':
      return `${actor} declined a change of plan to ${planChangeVerb(row.detail)} ${sprintNameOf(row.detail)}${reasonOf(row.detail, 'note')}`;
    default:
      return `${actor} — ${humanizeKind(row.kind)}`;
  }
}
